import * as React from "react"

type StatefulFieldRemountInterface = {
  // A component that needs to remount on external catalog update should use this value as a key.
  remountKey: number
  // Notify an external catalog update (undo, redo, reload)
  remount: () => void
}

const StatefulFieldRemount = React.createContext<StatefulFieldRemountInterface | undefined>(undefined)

/**
 * This context provides a key and a method to change this key.
 * It is used to remount controlled fields, when the catalog is modified externally (undo, redo, etc).
 * Controlled fields store their own state and cannot be updated externally, the only way is to re-mount them.
 * @param children
 * @constructor
 */

export const StatefulFieldRemountProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [remountKey, setRemountKey] = React.useState(0)

  const value = React.useMemo(
    () => ({
      remountKey,
      remount: () => setRemountKey((version) => version + 1),
    }),
    [remountKey],
  )

  return <StatefulFieldRemount.Provider value={value}>{children}</StatefulFieldRemount.Provider>
}

export function useStatefulFieldRemount(): StatefulFieldRemountInterface {
  const context = React.useContext(StatefulFieldRemount)

  if (context === undefined) {
    throw new Error(`useStatefulFieldRemount must be used within StatefulFieldRemountProvider`)
  }

  return context
}
